import React, { useState } from 'react';
import FormField from '../common/FormField';
import Button from '../common/Button';
import {
  DebitOrderMandateDTO,
  DonationDTO,
  MemberClient,
  MemberDTO,
  QuestionSetItemAnswerClient,
  QuestionSetItemAnswerDTO,
  QuestionSetItemDTO,
} from '../../services/apiClient';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';
import SimpleAlert from '../common/SimpleAlert';

interface Props {
  campaignId: string;
  member: MemberDTO;
  questionSetItems: QuestionSetItemDTO[];
  answers: QuestionSetItemAnswerDTO[];
  donation: DonationDTO;
  debitOrderMandate: DebitOrderMandateDTO;
  setAnswers: React.Dispatch<React.SetStateAction<QuestionSetItemAnswerDTO[]>>;
  onDebitOrderMandateChange: (updated: Partial<DebitOrderMandateDTO>) => void;
  onChange: (updated: Partial<MemberDTO>) => void;
  onSubmit: () => void;
  onBack: () => void;
}

const CaptureUserDetails: React.FC<Props> = ({
  campaignId,
  member,
  questionSetItems,
  answers,
  donation,
  setAnswers,
  onChange,
  onSubmit,
  onBack,
}) => {
  const [errors, setErrors] = useState<{
    email?: string;
    firstName?: string;
    lastName?: string;
    dynamicFields?: Record<string, string>;
  }>({ dynamicFields: {} });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const client = new MemberClient(API_BASE_URL, axiosInstance);
  const answerClient = new QuestionSetItemAnswerClient(API_BASE_URL, axiosInstance);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {

    const newErrors: typeof errors = { dynamicFields: {} };

    if (!member.email || !validateEmail(member.email)) {
      newErrors.email = 'Please enter a valid email address';
      setErrors(newErrors);
      return;
    }

    if (!member.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!member.lastName?.trim()) newErrors.lastName = 'Last name is required';

    // Dynamic field validation
    questionSetItems.forEach((item) => {
      if (!answers.find((a) => a.questionSetItemId === item.id)?.answer?.trim()) {
        newErrors.dynamicFields![item.id!] = 'This field is required';
      }
    });

    if (
      Object.keys(newErrors).length > 0 &&
      (newErrors.firstName || newErrors.lastName || Object.keys(newErrors.dynamicFields!).length > 0)
    ) {
      setErrors(newErrors);
      return;
    }

    setErrors({ dynamicFields: {} });
    setIsSubmitting(true);

    try {
      const result = await client.getMemberByEmail(member.email);
      if (result && result.success && result.data) {
        member = result.data
      }

      const response = member.id
        ? await client.updateMember(member.id, member)
        : await client.createMember(member);

      if (response && response.success && response.data) {
        const savedMember = response.data;
        onChange(savedMember);

        const enrichedAnswers = answers.map((a) => ({
          ...a,
          memberId: savedMember.id,
          campaignId,
        }));

        await Promise.all(
          enrichedAnswers.map((answer) => answerClient.createQuestionSetItemAnswer(answer))
        );

        await onSubmit();
      }
    } catch (error) {
      console.error('Failed to save member or answers:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAnswerChange = (questionSetItemId: string, answer: string) => {
    setAnswers((prevAnswers) => {
      const existing = prevAnswers.find((a) => a.questionSetItemId === questionSetItemId);
      if (existing) {
        return prevAnswers.map((a) =>
          a.questionSetItemId === questionSetItemId ? { ...a, answer } : a
        );
      } else {
        return [
          ...prevAnswers,
          { questionSetItemId, answer, memberId: member?.id, campaignId },
        ];
      }
    });

    // Clear dynamic field errors
    setErrors((prev) => {
      const updatedErrors = { ...prev };
      if (updatedErrors.dynamicFields) {
        delete updatedErrors.dynamicFields[questionSetItemId];
      }
      return updatedErrors;
    });
  };

  const renderDynamicField = (item: QuestionSetItemDTO) => {
    if (!item?.id || !item?.questionSetItemTypeName) return null;

    const fieldId = item.id;
    const label = item.question || '';
    const existingAnswer = answers.find((a) => a.questionSetItemId === fieldId)?.answer || '';
    const error = errors.dynamicFields?.[fieldId];

    switch (item.questionSetItemTypeName) {
      case 'Text box':
        return (
          <FormField
            key={fieldId}
            id={fieldId}
            label={label}
            type="text"
            value={existingAnswer}
            onChange={(e) => handleAnswerChange(fieldId, e.target.value)}
            error={error}
          />
        );

      case 'Drop down list': {
        const options = [
          { label: 'Please select', value: '' },
          ...((item.data ?? '')
            .split(',')
            .map((opt) => opt.trim())
            .filter(Boolean)
            .map((o) => ({ label: o, value: o }))),
        ];

        return (
          <FormField
            key={fieldId}
            id={fieldId}
            label={label}
            type="dropdown"
            value={existingAnswer}
            onChange={(e) => handleAnswerChange(fieldId, e.target.value)}
            options={options}
            error={error}
          />
        );
      }

      default:
        return null;
    }
  };

  const renderNetcashMessage = () => {
    if (donation.frequency === 'once') {
      return (
        <div>
          <SimpleAlert
            message="YOU WILL BE REDIRECTED TO NETCASH AND YOUR 1ST PAYMENT WILL BE TODAY."
            variant="info"
          />
        </div>
      )
    }
  }

  return (
    <div>

      <FormField
        id="email"
        label="Email"
        type="email"
        value={member.email || ''}
        onChange={(e) => {
          onChange({ email: e.target.value });
          if (errors.email)
            setErrors((prev) => ({ ...prev, email: undefined }));
        }}
        placeholder="Enter your email"
        error={errors.email}
      />

      <FormField
        id="firstName"
        label="First Name"
        type="text"
        value={member.firstName || ''}
        onChange={(e) => {
          onChange({ firstName: e.target.value });
          if (errors.firstName)
            setErrors((prev) => ({ ...prev, firstName: undefined }));
        }}
        placeholder="First Name"
        error={errors.firstName}
      />
      <FormField
        id="lastName"
        label="Last Name"
        type="text"
        value={member.lastName || ''}
        onChange={(e) => {
          onChange({ lastName: e.target.value });
          if (errors.lastName)
            setErrors((prev) => ({ ...prev, lastName: undefined }));
        }}
        placeholder="Last Name"
        error={errors.lastName}
      />

      {Array.isArray(questionSetItems) &&
        questionSetItems
          .sort((a, b) => (a.orderSequence ?? 0) - (b.orderSequence ?? 0))
          .map((item) => renderDynamicField(item))}

      {renderNetcashMessage()}

      <div className="mt-4 flex justify-between">
        <Button onClick={onBack} variant="secondary">
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default CaptureUserDetails;
